import * as React from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { AnimatePresence, motion, HTMLMotionProps } from 'framer-motion'
import { RemoveScroll } from 'react-remove-scroll'
import styled from 'styled-components'
import { useMatchBreakpoints } from '@pancakeswap/uikit'
import { maskConfig, modalConfig } from '../../config/motion'
import { cloneElement } from '../_util/reactNode'
import Button from '../Button'

const PortalWrapper = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  &.mask {
    background: rgba(0, 0, 0, 0.65);
  }
  &.scroll {
    margin: 0 auto;
    width: fit-content;
  }
  @media not screen and (max-width: 1280px) {
    > .inside {
      align-items: stretch;
      max-height: 88vh;
      border-radius: 16px;
      background: #292929;
      overflow: hidden;
      .header {
        padding: 0 24px;
        height: 72px;
        h4 {
          color: rgba(255, 255, 255, 1);
          font-size: 20px;
          font-weight: 700;
          line-height: 100%;
        }
        .clear {
          width: 24px;
          height: 24px;
          user-select: none;
          cursor: pointer;
          transition: all 0.3s ease-in-out;
          &:hover {
            opacity: 0.6;
          }
        }
      }
      .content {
        flex: 1;
        overflow-x: hidden;
        overflow-y: auto;
        &::-webkit-scrollbar {
          width: 0;
          height: 0;
          background-color: transparent;
        }
      }
      .footer {
        gap: 12px;
        padding: 32px 24px 24px 24px;
        button {
          height: 52px;
        }
        span {
          line-height: 52px;
          font-size: 20px;
        }
      }
    }
  }
  @media screen and (max-width: 1280px) {
    justify-content: flex-end !important;
    padding-top: 64px;
    > .inside {
      padding-bottom: var(--theme-base-safe-area);
      width: 100vw;
      align-items: stretch;
      border-radius: 16px 16px 0 0;
      background: #292929;
      overflow: hidden;
      .header {
        padding: 16px 16px 8px 16px;
        h4 {
          color: rgba(255, 255, 255, 1);
          font-size: 16px;
          font-weight: 700;
          line-height: 100%;
        }
        .clear {
          width: 20px;
          height: 20px;
          user-select: none;
          cursor: pointer;
          transition: all 0.3s ease-in-out;
          &:hover {
            opacity: 0.6;
          }
        }
      }
      .content {
        flex: 1;
        overflow-x: hidden;
        overflow-y: auto;
        &::-webkit-scrollbar {
          width: 0;
          height: 0;
          background-color: transparent;
        }
      }
      .footer {
        gap: 12px;
        padding: 16px 16px 32px 16px;
      }
    }
  }
`

export const motionConfig: HTMLMotionProps<any> = {
  initial: { opacity: 0, y: '40%' },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: '40%' },
}

export interface ModalProps {
  className?: string
  visible?: boolean
  mask?: boolean
  scroll?: boolean
  forceRender?: React.ReactNode
  title?: React.ReactNode
  closable?: boolean
  loading?: boolean
  disabled?: boolean
  cancel?: React.ReactNode
  ok?: React.ReactNode
  children?: React.ReactNode
  onClose?: (...args: any[]) => any
  onCancel?: (...args: any[]) => any
  onOk?: (...args: any[]) => any
}

const Portal: React.FC<ModalProps> = (props: ModalProps) => {
  const {
    className,
    mask = true,
    scroll = false,
    forceRender,
    title,
    closable = true,
    cancel,
    ok,
    children,
    onClose,
    onCancel,
    onOk,
    ...rest
  } = props

  const classes = classNames(className, 'flex flex-col items-center justify-center', { mask, scroll })
  const { isMobile } = useMatchBreakpoints()

  const forceRef = React.useRef<HTMLDivElement>(null)

  const handleClose = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // eslint-disable-next-line no-unused-expressions
      onClose?.(e) ?? onCancel?.(e)
    },
    [onClose, onCancel],
  )

  const handleCancel = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      onCancel?.(e)
    },
    [onCancel],
  )

  const handleOk = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onOk?.(e)
    },
    [onOk],
  )

  const [forceHeight, setForceHeight] = React.useState<number>(0)

  const forcePosition = React.useCallback(() => {
    if (!forceRef.current) return
    const { offsetHeight } = forceRef.current
    if (forceHeight !== 0) return
    setForceHeight(offsetHeight)
  }, [forceRef, forceHeight])

  React.useEffect(() => {
    forcePosition()
  }, [forceRef, forcePosition])

  const renderHeader = React.useMemo(() => {
    if (forceRender) return cloneElement(forceRender, { ref: forceRef })
    if (!title && !closable) return null
    return (
      <div className="header flex flex-row items-center justify-between">
        <h4>{title}</h4>
        {closable && (
          <img
            className="clear"
            src="/newImages/launchpad/icon-close.svg"
            alt="icon"
            onClick={handleClose}
            aria-hidden
          />
        )}
      </div>
    )
  }, [forceRender, forceRef, title, closable, handleClose])

  const renderFooter = React.useMemo(() => {
    if (!cancel && !ok) return null
    return (
      <div className="footer flex flex-row items-center justify-between">
        {cancel && (
          <Button type="second" onClick={handleCancel}>
            {cancel}
          </Button>
        )}
        {ok && (
          <Button {...rest} onClick={handleOk}>
            {ok}
          </Button>
        )}
      </div>
    )
  }, [cancel, ok, handleCancel, handleOk, rest])

  const testMotion = React.useMemo(() => {
    return isMobile ? motionConfig : modalConfig
  }, [isMobile])

  const memoElement = React.useMemo(() => {
    const renderPortal = (
      <PortalWrapper className={classes} {...maskConfig}>
        <motion.div className="inside col-center" {...testMotion}>
          {renderHeader}
          <div className="content">{cloneElement(children)}</div>
          {renderFooter}
        </motion.div>
      </PortalWrapper>
    )

    return scroll ? renderPortal : <RemoveScroll>{renderPortal}</RemoveScroll>
  }, [classes, renderHeader, children, renderFooter, scroll, testMotion])

  return createPortal(memoElement, window.document.body)
}

const Modal: React.FC<ModalProps> = (props: ModalProps) => {
  const { visible = false } = props

  return <AnimatePresence>{visible && <Portal {...props} />}</AnimatePresence>
}

export default Modal
